/**
 * Scenarios API Service
 * 
 * ✅ BEST PRACTICE: Pure API functions - no Vue, no Vue Query, no reactivity
 * This layer only knows about HTTP requests and responses
 * 
 * Usage:
 *   import { getScenarioCosts, getNetworkData } from '@/features/scenarios'
 */

import { apiClient } from '@/core/api'

// ============================================================================
// SCENARIO COSTS & RESULTS
// ============================================================================

/**
 * Fetch scenario costs/aggregates from backend
 * Pure API function - no Vue Query, no reactivity
 */
export async function getScenarioCosts(scenarioId, resultType, workspaceId) {
  const response = await apiClient.get(`/scenarios/${scenarioId}/results`, {
    params: {
      workspace_id: workspaceId,
      result_type: resultType
    }
  })

  const data = response.data || {}

  // Normalize the response - include ALL fields the backend provides
  const normalized = {
    scenario_id: scenarioId,
    result_type: resultType,
    total_cost: data.total_cost || 0,
    fixed_cost: data.fixed_cost || 0,
    variable_cost: data.variable_cost || 0,
    shipping_cost: data.transport_cost || data.shipping_cost || 0,
    holding_cost: data.holding_cost || 0,
    total_demand: data.total_demand || 0,
    demand_fulfilled: data.demand_fulfilled || 0,
    service_level: data.service_level || 0,
    service_level_per_customer: data.service_level_per_customer || data.service_level || 0,
    fulfillment_percentage: data.demand_fulfilled > 0 && data.total_demand > 0
      ? (data.demand_fulfilled / data.total_demand) * 100
      : (data.demand_fulfilled || 0),
    product_count: data.product_count || 0,
    // KPI fields that were previously missing - needed for capacity utilization, open DCs
    open_dcs: data.open_dcs || 0,
    avg_capacity_utilization: data.avg_capacity_utilization || 0,
    total_capacity: data.total_capacity || 0,
    units_processed: data.units_processed || 0
  }
  
  // Preserve analysis field if it exists (for simulation results with percentiles)
  if (data.analysis) {
    normalized.analysis = data.analysis
  }
  
  // Also preserve any percentile fields directly on the data object
  if (data.total_cost_p10 !== undefined) normalized.total_cost_p10 = data.total_cost_p10
  if (data.total_cost_p50 !== undefined) normalized.total_cost_p50 = data.total_cost_p50
  if (data.total_cost_p90 !== undefined) normalized.total_cost_p90 = data.total_cost_p90
  if (data.service_level_p10 !== undefined) normalized.service_level_p10 = data.service_level_p10
  if (data.service_level_p50 !== undefined) normalized.service_level_p50 = data.service_level_p50
  if (data.service_level_p90 !== undefined) normalized.service_level_p90 = data.service_level_p90
  
  return normalized
}

// ============================================================================
// NETWORK DATA
// ============================================================================

/**
 * Fetch network data (facilities, lanes, customers)
 * Pure API function - no Vue Query, no reactivity
 */
export async function getNetworkData(scenarioId, mode, workspaceId) {
  const response = await apiClient.get('/network/data', {
    params: {
      scenario_id: scenarioId,
      mode,
      workspace_id: workspaceId
    }
  })
  
  const data = response.data || {}
  
  // ✅ CRITICAL: If backend says no_results, return empty arrays
  if (data.no_results) {
    // No results for this scenario
    return {
      facilities: [],
      lanes: [],
      customers: [],
      transport_modes: [],
      scenario_id: scenarioId,
      workspace_id: workspaceId,
      mode,
      no_results: true
    }
  }

  return {
    facilities: data.facilities || [],
    lanes: data.lanes || [],
    customers: data.customers || [],
    transport_modes: data.transport_modes || [],
    scenario_id: scenarioId,
    workspace_id: workspaceId,
    mode,
    no_results: false
  }
}

// ============================================================================
// SCENARIOS LIST
// ============================================================================

/**
 * Fetch all scenarios for a workspace
 */
export async function getScenarios(workspaceId) {
  const response = await apiClient.get('/scenarios', {
    params: { workspace_id: workspaceId }
  })
  
  return response.data?.scenarios || response.data || []
}

/**
 * Fetch scenario catalog (static definitions from scenarios.json)
 * This is the source of truth for all scenario metadata.
 * Can be cached as it rarely changes.
 */
export async function getScenariosCatalog() {
  const response = await apiClient.get('/scenarios/catalog')
  
  return response.data?.scenarios || response.data || []
}

/**
 * Fetch scenario runtime status (lightweight, workspace-specific)
 * Returns only runtime state (status, timestamps, progress).
 * Much faster than full scenario endpoint.
 */
export async function getScenariosStatus(workspaceId) {
  const response = await apiClient.get('/scenarios/status', {
    params: { workspace_id: workspaceId }
  })
  
  return response.data?.scenarios || {} // Returns a map: {scenario_id: {status, ...}}
}

/**
 * Fetch prebuilt scenarios (OPTIMIZED: merges catalog + status client-side)
 * 
 * This function:
 * 1. Fetches static catalog from scenarios.json (cacheable)
 * 2. Fetches runtime status from database (lightweight)
 * 3. Merges them client-side
 * 
 * Benefits:
 * - Faster: catalog can be cached, status query is lightweight
 * - More reliable: catalog is source of truth, guarantees all scenarios
 * - Scalable: catalog served as static content, only status hits DB
 */
export async function getPrebuiltScenarios(workspaceId) {
  // Fetch both catalog and status in parallel for prebuilt scenarios
  
  // Fetch both catalog and status in parallel
  const [catalogResponse, statusResponse] = await Promise.all([
    apiClient.get('/scenarios/catalog'),
    apiClient.get('/scenarios/status', {
      params: { workspace_id: workspaceId }
    })
  ])
  
  const catalog = catalogResponse.data?.scenarios || []
  const statusMap = statusResponse.data?.scenarios || {} // {scenario_id: {status, ...}}
  
  // Merge catalog + status client-side
  const merged = catalog.map(scenario => {
    const statusData = statusMap[scenario.id] || {}
    
    // If user edited the scenario name (saved in DB), prefer that over catalog name
    // The DB scenario_name will differ from catalog name when user has edited it
    const displayName = statusData.scenario_name || scenario.name
    
    return {
      ...scenario,
      // Override name with user-edited name from DB if available
      name: displayName,
      // Merge runtime status
      status: statusData.status || 'not_run',
      last_run_at: statusData.last_run_at || null,
      run_time_seconds: statusData.run_time_seconds || null,
      current_step: statusData.current_step || 0,
      total_steps: statusData.total_steps || 6,
      step_description: statusData.step_description || null,
      error_message: statusData.error_message || null,
      // For backward compatibility, use status for both optimization_status and simulation_status
      // These can be computed later if needed based on result existence
      optimization_status: statusData.status || 'not_run',
      simulation_status: statusData.status || 'not_run',
    }
  })
  
  // Filter to only prebuilt scenarios (ID >= 3)
  const prebuilt = merged.filter(s => s.is_prebuilt && s.id >= 3)
  
  // Merged catalog + status
  
  return prebuilt
}

// ============================================================================
// FACILITIES & FLOWS
// ============================================================================

/**
 * Fetch facilities breakdown for a scenario
 * 🆕 COMPREHENSIVE CACHING: Accept resultType parameter
 */
export async function getFacilitiesBreakdown(scenarioId, workspaceId, resultType = 'optimization') {
  // Fetch facilities breakdown
  
  const response = await apiClient.get(`/scenarios/${scenarioId}/facilities-breakdown`, {
    params: { 
      workspace_id: workspaceId,
      result_type: resultType  // 🆕 Pass result_type to backend
    }
  })
  
  return response.data?.facilities || []
}

/**
 * Fetch facility results (optimization results for a facility)
 */
export async function getFacilityResults(facilityName, scenarioId, workspaceId) {
  // Fetch facility results
  
  const response = await apiClient.get(`/facilities/${facilityName}/results`, {
    params: {
      scenario_id: scenarioId,
      workspace_id: workspaceId
    }
  })
  
  return response.data || {}
}

/**
 * Fetch facility flows (inbound/outbound)
 * @param {boolean} showSimulation - If true, filter for is_simulation = 1
 */
export async function getFacilityFlows(facilityName, scenarioId, workspaceId, showSimulation = false) {
  // Fetch facility flows
  
  const response = await apiClient.get(`/facilities/${facilityName}/flows`, {
    params: {
      scenario_id: scenarioId,
      workspace_id: workspaceId,
      show_simulation: showSimulation
    }
  })
  
  return {
    inbound: response.data?.inbound || [],
    outbound: response.data?.outbound || []
  }
}

// ============================================================================
// STATISTICS
// ============================================================================

/**
 * Fetch purchase method statistics
 */
export async function getPurchaseMethodStats(scenarioId, workspaceId) {
  // Fetch purchase method stats
  
  const response = await apiClient.get(`/purchase-method/stats/${scenarioId}`, {
    params: { workspace_id: workspaceId }
  })
  
  if (response.data?.status === 'success' && response.data?.data) {
    return response.data.data
  }
  
  return {
    methods: [],
    totalFlow: 0,
    skuCount: 0,
    shippingCost: 0
  }
}

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Fetch optimization configuration
 */
export async function getOptimizationConfig(scenarioId, workspaceId) {
  // Fetch optimization config
  
  const response = await apiClient.get('/optimization/config/get', {
    params: {
      scenario_id: scenarioId,
      workspace_id: workspaceId
    }
  })
  
  return response.data || {}
}

/**
 * Fetch simulation/uncertainty configuration
 */
export async function getSimulationConfig(scenarioId, workspaceId) {
  // Fetch simulation config
  
  const response = await apiClient.get('/simulation/config', {
    params: {
      scenario_id: scenarioId,
      workspace_id: workspaceId
    }
  })
  
  return response.data || {}
}

// ============================================================================
// SCENARIO OPERATIONS (MUTATIONS)
// ============================================================================

/**
 * Run scenarios (batch operation)
 */
export async function runScenarios(params) {
  const { scenarioIds = [], runAll = false, workspaceId } = params
  
  const payload = runAll ? {} : { scenario_ids: scenarioIds }
  // ✅ FIX: Use !== undefined instead of truthiness check (workspace_id=0 is valid)
  if (workspaceId !== undefined && workspaceId !== null) {
    payload.workspace_id = workspaceId
  }
  
  console.log('🚀 [API] runScenarios payload:', JSON.stringify(payload))
  const response = await apiClient.post('/scenarios/run-all', payload)
  return response.data
}

/**
 * Get batch status
 */
export async function getBatchStatus() {
  const response = await apiClient.get('/scenarios/batch-status')
  return response.data
}

/**
 * Update scenario name
 */
export async function updateScenarioName(scenarioId, name, workspaceId) {
  const response = await apiClient.put(`/scenarios/${scenarioId}/name`, {
    name,
    workspace_id: workspaceId
  })
  return response.data
}

/**
 * Reset all scenario statuses
 */
export async function resetAllStatuses(workspaceId) {
  const response = await apiClient.post('/scenarios/reset-all-statuses', {
    workspace_id: workspaceId
  })
  return response.data
}

// ============================================================================
// CUSTOM SCENARIOS
// ============================================================================

/**
 * Get custom scenarios
 */
export async function getCustomScenarios(workspaceId) {
  const response = await apiClient.get('/scenarios/custom', {
    params: { workspace_id: workspaceId }
  })
  return response.data?.scenarios || []
}

/**
 * Import custom scenario
 */
export async function importCustomScenario(formData) {
  const response = await apiClient.post('/scenarios/custom/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return response.data
}

/**
 * Create custom scenario from optimized baseline
 */
export async function createCustomScenarioFromOptimized(params) {
  const { name, description, workspaceId } = params
  const response = await apiClient.post('/scenarios/custom/from-optimized', {
    name,
    description,
    workspace_id: workspaceId
  })
  return response.data
}

/**
 * Run custom scenario
 */
export async function runCustomScenario(scenarioId, workspaceId) {
  const response = await apiClient.post(`/scenarios/custom/${scenarioId}/run`, {
    workspace_id: workspaceId
  })
  return response.data
}

/**
 * Delete custom scenario
 */
export async function deleteCustomScenario(scenarioId, workspaceId) {
  const response = await apiClient.delete(`/scenarios/custom/${scenarioId}`, {
    params: { workspace_id: workspaceId }
  })
  return response.data
}

/**
 * Get scenario comparison data (for comparison bar)
 */
export async function getScenarioComparison(workspaceId) {
  // Fetch scenario comparison
  
  const response = await apiClient.get('/scenarios/comparison', {
    params: { workspace_id: workspaceId }
  })
  
  return response.data?.scenarios || []
}

/**
 * Get scenario bundle (complete scenario data)
 */
export async function getScenarioBundle(scenarioId, workspaceId) {
  // Fetch scenario bundle
  
  const response = await apiClient.get(`/scenarios/${scenarioId}/bundle`, {
    params: { workspace_id: workspaceId }
  })
  
  return response.data?.data || response.data
}

/**
 * Get scenario flow results
 */
export async function getScenarioFlowResults(scenarioId, workspaceId) {
  // Fetch flow results
  
  const response = await apiClient.get(`/scenarios/${scenarioId}/flow-results`, {
    params: { workspace_id: workspaceId }
  })
  
  return response.data || []
}





